import { Injectable } from '@angular/core';
import { Workbook, Worksheet } from 'exceljs';
import { saveAs } from 'file-saver';
import { Organization, OrgPart, OrgHat, OrgUser } from '@newgenus/common';

/**
 * Schema version of the workbook. Bumped when the column shape changes in a
 * non-additive way. The importer (P3) refuses to load anything newer than the
 * version it knows about, and refuses to load `< 1`.
 */
export const ORG_EXCEL_SCHEMA_VERSION = 2 as const;

/** Sheet names — literal to keep import/export symmetric. */
export const SHEET_STRUCTURE = 'Structure';
export const SHEET_STAFF = 'Staff';
export const SHEET_META = '_Meta';

/**
 * One row of the `Structure` sheet — see Plan §16.6 (Excel format spec).
 *
 * Sibling order is preserved by **row order** within the sheet. The importer
 * rebuilds `OrgPart.id` from row position; this is why we don't export it.
 *
 * `partKey` and `parentPartKey` are the canonical identifiers and are
 * round-trip safe. A user editing a name or moving a row in Excel must NOT
 * touch the key columns.
 */
export interface StructureRow {
    partKey: string;
    parentPartKey: string;
    partType: string;
    name: string;
    headerRole: '' | 'head' | 'deputy' | 'assistant';
    colourBg: string;
    colourFg: string;
    colourLink: string;
    vfp: string;
    /** Read-only convenience: full breadcrumb so a human editor can find rows. */
    path: string;
    /**
     * Schema v2 — at most one row per workbook may carry `true`. Round-trips
     * the org-wide CEO singleton (see plan addendum 19). Empty on legacy v1
     * imports; populated automatically on first export of an org whose CEO
     * post has `isCeoPost: true`.
     */
    isCeoPost: boolean;
}

/**
 * One row of the `Staff` sheet — many-to-one (multiple rows may carry the same
 * email, e.g. an "HFA" person who holds several posts).
 *
 * `postPartKey` is the canonical link. `postPath` is human-aided so an editor
 * can identify the post without looking up keys; the importer prefers
 * `postPartKey` when both are present, and falls back to path-matching.
 */
export interface StaffRow {
    email: string;
    firstName: string;
    lastName: string;
    /** `head` posts get `manager`, others `member`. The user can override. */
    role: 'owner' | 'manager' | 'member';
    postPartKey: string;
    postPath: string;
}

/**
 * Single-row `_Meta` sheet — informational only, never read on import for
 * structural decisions. `schemaVersion` is the gate.
 */
export interface MetaRow {
    schemaVersion: number;
    exportedAt: string;
    sourceOrgKey: string;
    sourceOrgName: string;
    description: string;
}

/**
 * Org → Excel exporter.
 *
 * Pure transform — no Firestore reads, no network. Uses only the data already
 * on the `Organization` input the org-board component receives:
 *   - `org.orgParts: OrgPart[]`
 *   - `org.orgHats[hatKey]: OrgHat` + `org.orgHats.hatsKeys: string[]`
 *   - `org.orgUsers[uid]: OrgUser`  + `org.orgUsers.userKeys: string[]`
 *
 * Plan reference: §16.6 (Excel format spec) and Step 3 toolbar in §10.
 *
 * Two sheets + a hidden `_Meta`:
 *   - `Structure` — one row per part. Sibling order preserved by row order.
 *   - `Staff`     — one row per (user, post) assignment. Many-to-one supported.
 *   - `_Meta`     — schema version, exported-at, source org info.
 *
 * The exporter is deterministic for a given org snapshot — re-exporting the
 * same org produces a byte-identical workbook (modulo the `exportedAt`
 * timestamp on `_Meta`). This makes round-trip testing trivial.
 */
@Injectable({ providedIn: 'root' })
export class OrgExcelService {
    /**
     * Build & download an `.xlsx` for the given organization.
     *
     * The returned promise resolves once `saveAs` has been called — the
     * browser's "Save As" dialog (or auto-download) is fire-and-forget after
     * that point. Callers can await this to chain a snackbar / progress UI.
     */
    public async exportOrganization(org: Organization): Promise<void> {
        const workbook = await this.buildWorkbook(org);
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const filename = this.suggestedFilename(org);
        saveAs(blob, filename);
    }

    /**
     * Build the workbook in memory without triggering a download. Useful for
     * tests, snapshot fixtures, and the eventual "Publish as template" flow.
     */
    public async buildWorkbook(org: Organization): Promise<Workbook> {
        const wb = new Workbook();
        wb.creator = 'Newgenus';
        wb.created = new Date();

        const structureRows = this.buildStructureRows(org);
        const staffRows = this.buildStaffRows(org, structureRows);
        const meta = this.buildMeta(org);

        this.writeStructureSheet(wb.addWorksheet(SHEET_STRUCTURE), structureRows);
        this.writeStaffSheet(wb.addWorksheet(SHEET_STAFF), staffRows);
        this.writeMetaSheet(wb.addWorksheet(SHEET_META), meta);

        return wb;
    }

    /** Filename pattern: `<org-name>-orgboard-YYYYMMDD.xlsx` (sluggified). */
    public suggestedFilename(org: Organization): string {
        const slug = (org?.name ?? 'organization')
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '')
            .slice(0, 60) || 'organization';
        const d = new Date();
        const stamp = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
        return `${slug}-orgboard-${stamp}.xlsx`;
    }

    // ─────────────────────────────────────────────────────────────────────
    // Row builders
    // ─────────────────────────────────────────────────────────────────────

    private buildStructureRows(org: Organization): StructureRow[] {
        const parts = (org.orgParts ?? []).slice();
        const byParent = new Map<string, OrgPart[]>();
        for (const p of parts) {
            const list = byParent.get(p.parentPartKey ?? '') ?? [];
            list.push(p);
            byParent.set(p.parentPartKey ?? '', list);
        }
        for (const list of byParent.values()) {
            list.sort((a, b) => (a.id ?? 0) - (b.id ?? 0));
        }

        // DFS so siblings stay grouped under their parent — matches the visual
        // org-board reading order.
        const rows: StructureRow[] = [];
        const nameByKey = new Map(parts.map((p) => [p.partKey, p.name ?? '']));
        const parentByKey = new Map(parts.map((p) => [p.partKey, p.parentPartKey ?? '']));

        const buildPath = (partKey: string): string => {
            const segments: string[] = [];
            let cur: string | undefined = partKey;
            const guard = new Set<string>();
            while (cur && !guard.has(cur)) {
                guard.add(cur);
                segments.unshift(nameByKey.get(cur) ?? cur);
                cur = parentByKey.get(cur);
                if (!cur) break;
            }
            return segments.join(' \u203a ');
        };

        const visit = (parentKey: string): void => {
            for (const p of byParent.get(parentKey) ?? []) {
                rows.push({
                    partKey: p.partKey,
                    parentPartKey: p.parentPartKey ?? '',
                    partType: (p.partType ?? '').toLowerCase(),
                    name: p.name ?? '',
                    headerRole: (p.headerRole as StructureRow['headerRole']) ?? '',
                    colourBg: p.colour?.background ?? '',
                    colourFg: p.colour?.color ?? '',
                    colourLink: p.colour?.linkColor ?? '',
                    vfp: p.vfp ?? '',
                    path: buildPath(p.partKey),
                    isCeoPost: !!(p as { isCeoPost?: boolean }).isCeoPost,
                });
                visit(p.partKey);
            }
        };
        visit('');

        // Orphans — parts whose parent is missing from the parts array.
        // Surfaced as top-level rows so nothing is silently dropped.
        const seen = new Set(rows.map((r) => r.partKey));
        for (const p of parts) {
            if (!seen.has(p.partKey)) {
                rows.push({
                    partKey: p.partKey,
                    parentPartKey: p.parentPartKey ?? '',
                    partType: (p.partType ?? '').toLowerCase(),
                    name: p.name ?? '',
                    headerRole: (p.headerRole as StructureRow['headerRole']) ?? '',
                    colourBg: p.colour?.background ?? '',
                    colourFg: p.colour?.color ?? '',
                    colourLink: p.colour?.linkColor ?? '',
                    vfp: p.vfp ?? '',
                    path: '(orphan) ' + (p.name ?? ''),
                    isCeoPost: !!(p as { isCeoPost?: boolean }).isCeoPost,
                });
            }
        }
        return rows;
    }

    private buildStaffRows(org: Organization, structure: StructureRow[]): StaffRow[] {
        const rows: StaffRow[] = [];
        const pathByPartKey = new Map(structure.map((r) => [r.partKey, r.path]));
        const headerRoleByPartKey = new Map(structure.map((r) => [r.partKey, r.headerRole]));

        const hatsKeys: string[] = (org.orgHats as any)?.hatsKeys ?? [];
        const orgUsers: Record<string, OrgUser> = (org.orgUsers as any) ?? {};

        for (const hatKey of hatsKeys) {
            const hat = (org.orgHats as any)?.[hatKey] as OrgHat | undefined;
            if (!hat) continue;
            const postKeys: string[] = hat.positionArray ?? [];
            const assigned: string[] = hat.assignedToArray ?? [];

            for (const uid of assigned) {
                const user = orgUsers[uid];
                if (!user) continue;
                const { firstName, lastName } = this.splitName(user.name ?? '');
                for (const postKey of postKeys.length > 0 ? postKeys : ['']) {
                    const headerRole = headerRoleByPartKey.get(postKey) ?? '';
                    rows.push({
                        email: user.email ?? '',
                        firstName,
                        lastName,
                        role: headerRole === 'head' ? 'manager' : 'member',
                        postPartKey: postKey,
                        postPath: pathByPartKey.get(postKey) ?? '',
                    });
                }
            }
        }

        // Stable sort: by post path, then by lastName, then email — keeps diffs
        // readable for round-trip testing.
        rows.sort((a, b) =>
            a.postPath.localeCompare(b.postPath) ||
            a.lastName.localeCompare(b.lastName) ||
            a.email.localeCompare(b.email),
        );
        return rows;
    }

    private buildMeta(org: Organization): MetaRow {
        return {
            schemaVersion: ORG_EXCEL_SCHEMA_VERSION,
            exportedAt: new Date().toISOString(),
            sourceOrgKey: (org as any).key ?? '',
            sourceOrgName: org.name ?? '',
            description:
                'Newgenus org-board export. Edit `Structure` rows to rename / recolour / reorder; ' +
                'edit `Staff` rows to add or remove people. Do not modify `partKey` / `parentPartKey`. ' +
                'Re-import via the wizard\u2019s Step 1 \u201cImport from Excel\u201d card.',
        };
    }

    // ─────────────────────────────────────────────────────────────────────
    // Sheet writers
    // ─────────────────────────────────────────────────────────────────────

    private writeStructureSheet(ws: Worksheet, rows: StructureRow[]): void {
        ws.columns = [
            { header: 'partKey', key: 'partKey', width: 28 },
            { header: 'parentPartKey', key: 'parentPartKey', width: 28 },
            { header: 'partType', key: 'partType', width: 12 },
            { header: 'name', key: 'name', width: 36 },
            { header: 'headerRole', key: 'headerRole', width: 12 },
            { header: 'colourBg', key: 'colourBg', width: 10 },
            { header: 'colourFg', key: 'colourFg', width: 10 },
            { header: 'colourLink', key: 'colourLink', width: 10 },
            { header: 'vfp', key: 'vfp', width: 60 },
            { header: 'path', key: 'path', width: 60 },
            { header: 'isCeoPost', key: 'isCeoPost', width: 10 },
        ];
        ws.addRows(rows);
        this.styleHeaderRow(ws);
        ws.views = [{ state: 'frozen', ySplit: 1 }];
    }

    private writeStaffSheet(ws: Worksheet, rows: StaffRow[]): void {
        ws.columns = [
            { header: 'email', key: 'email', width: 32 },
            { header: 'firstName', key: 'firstName', width: 18 },
            { header: 'lastName', key: 'lastName', width: 18 },
            { header: 'role', key: 'role', width: 10 },
            { header: 'postPartKey', key: 'postPartKey', width: 28 },
            { header: 'postPath', key: 'postPath', width: 60 },
        ];
        ws.addRows(rows);
        this.styleHeaderRow(ws);
        ws.views = [{ state: 'frozen', ySplit: 1 }];
    }

    private writeMetaSheet(ws: Worksheet, meta: MetaRow): void {
        ws.columns = [
            { header: 'key', key: 'k', width: 18 },
            { header: 'value', key: 'v', width: 80 },
        ];
        ws.addRows([
            { k: 'schemaVersion', v: meta.schemaVersion },
            { k: 'exportedAt', v: meta.exportedAt },
            { k: 'sourceOrgKey', v: meta.sourceOrgKey },
            { k: 'sourceOrgName', v: meta.sourceOrgName },
            { k: 'description', v: meta.description },
        ]);
        this.styleHeaderRow(ws);
        ws.state = 'hidden';
    }

    private styleHeaderRow(ws: Worksheet): void {
        const header = ws.getRow(1);
        header.font = { bold: true };
        header.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFE3F2FD' },
        };
        header.commit();
    }

    // ─────────────────────────────────────────────────────────────────────
    // Helpers
    // ─────────────────────────────────────────────────────────────────────

    private splitName(full: string): { firstName: string; lastName: string } {
        const trimmed = (full ?? '').trim();
        if (!trimmed) return { firstName: '', lastName: '' };
        const idx = trimmed.lastIndexOf(' ');
        if (idx < 0) return { firstName: trimmed, lastName: '' };
        return { firstName: trimmed.slice(0, idx), lastName: trimmed.slice(idx + 1) };
    }
}
